import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import { resetPassword } from "api/services/auth";
import Button from "components/Button";
import InputField from "components/FormFields/InputField";
import AuthLayout from "layouts/AuthLayout";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { setUser } from "store/user";

function containsUpperCase(str) {
  return /[A-Z]/.test(str);
}

function containsLowerCase(str) {
  return /[a-z]/.test(str);
}

function containsNumber(str) {
  return /\d/.test(str);
}

function isAtLeastEightChars(str) {
  return str.length >= 8;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const privateCode = searchParams.get("code");
  const dispatch = useDispatch();

  const [error, setError] = useState({
    password: {
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasEightChars: false,
    },
    passwordConfirmation: {
      isSame: false,
    },
  });

  const resetPassMutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: ({ data }) => {
      const user = data?.user;

      if (user?.id) {
        dispatch(setUser(user));
        localStorage.setItem("authToken", data?.jwt);
        localStorage.setItem("user", JSON.stringify(user));
        navigate("/");
      }
    },
  });

  function handlePasswordChange(e) {
    const password = e.target.value;
    const hasUpperCase = containsUpperCase(password);
    const hasLowerCase = containsLowerCase(password);
    const hasNumber = containsNumber(password);
    const hasEightChars = isAtLeastEightChars(password);

    setError((prev) => ({
      ...prev,
      password: {
        hasUpperCase,
        hasLowerCase,
        hasNumber,
        hasEightChars,
      },
    }));

    if (!e.target.value) {
      e.target.setCustomValidity("Bu sahə boş ola bilməz");
    } else if (!hasUpperCase) {
      e.target.setCustomValidity("Ən azı bir böyük hərf olmalıdır");
    } else if (!hasLowerCase) {
      e.target.setCustomValidity("Ən azı bir kiçik hərf olmalıdır");
    } else if (!hasNumber) {
      e.target.setCustomValidity("Ən azı bir rəqəm olmalıdır");
    } else if (!hasEightChars) {
      e.target.setCustomValidity("Ən azı 8 simvol olmalıdır");
    } else {
      e.target.setCustomValidity("");
    }
  }

  function handlePasswordConfirmationChange(e) {
    const password = e.target.value;
    setError((prev) => ({
      ...prev,
      passwordConfirmation: {
        isSame: password === e.target.form.password.value,
      },
    }));

    if (!e.target.value) {
      e.target.setCustomValidity("Bu sahə boş ola bilməz");
    } else if (e.target.value !== e.target?.form?.password?.value) {
      e.target.setCustomValidity("Şifrə ilə eyni olmalıdır");
    } else {
      e.target.setCustomValidity("");
    }
  }

  if (!privateCode) return <Navigate to="/login" replace />;

  const statusColors = {
    true: "text-green-300",
    false: "text-red-300",
  };

  const passwordHint = (
    <span>
      Ən azı bir{" "}
      <span className={`${statusColors[error.password.hasNumber]}`}>rəqəm</span>{" "}
      ,{" "}
      <span className={`${statusColors[error.password.hasUpperCase]}`}>
        böyük
      </span>{" "}
      və{" "}
      <span className={`${statusColors[error.password.hasLowerCase]}`}>
        kiçik{" "}
      </span>
      hərf və{" "}
      <span className={`${statusColors[error.password.hasEightChars]}`}>
        ən azı 8
      </span>{" "}
      və ya daha çox simvol olmalıdır
    </span>
  );

  const isPasswordConfirmationInvalid =
    error.passwordConfirmation.touched && !error.passwordConfirmation.isSame;

  const isValidationError =
    resetPassMutation.isError &&
    resetPassMutation.error?.response?.data?.error?.name == "ValidationError";

  return (
   <AuthLayout>
    <div className="p-4 rounded-tr-4xl z-10">
          <form
            className="my-4"
            onSubmit={(e) => {
              e.preventDefault();

              if (
                !error.password.hasUpperCase ||
                !error.password.hasLowerCase ||
                !error.password.hasNumber ||
                !error.password.hasEightChars
              )
                return;
              if (!error.passwordConfirmation?.isSame) {
                setError((prev) => ({
                  ...prev,
                  passwordConfirmation: {
                    isSame: false,
                    touched: true,
                  },
                }));

                return;
              }

              resetPassMutation.mutate({
                code: privateCode,
                password: e.target?.password?.value,
                passwordConfirmation: e.target?.password?.value,
              });
            }}
          >
            <InputField
              id="password"
              autoComplete="new-password"
              name="password"
              type="password"
              onChange={handlePasswordChange}
              required
              invalid={
                !error.password.hasUpperCase ||
                !error.password.hasLowerCase ||
                !error.password.hasNumber ||
                !error.password.hasEightChars
              }
              minLength="8"
              pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
              hint={passwordHint}
              placeholder="Yeni şifrəni daxil edin"
              label="Yeni şifrə"
            />

            <InputField
              id="passwordConfirmation"
              name="passwordConfirmation"
              type="password"
              required
              onChange={handlePasswordConfirmationChange}
              onBlur={(e) => {
                setError((prev) => ({
                  ...prev,
                  passwordConfirmation: {
                    isSame: e.target.value === e.target.form.password.value,
                    touched: true,
                  },
                }));
              }}
              invalid={isPasswordConfirmationInvalid}
              hint={
                error.passwordConfirmation?.touched &&
                !error.passwordConfirmation.isSame && (
                  <span
                    className={`${
                      statusColors[error.passwordConfirmation.isSame]
                    }`}
                  >
                    Şifrə ilə eyni olmalıdır
                  </span>
                )
              }
              minLength="8"
              placeholder="Şifrəni təkrar daxil edin"
              label="Şifrə təkrarı"
            />
            {isValidationError ? (
              <div className="mt-2 relative">
                <p className="text-red-500">
                  Bu link aktiv deyil.{" "}
                  <Link
                    to="/forgot-password"
                    className="text-sky-500 hover:underline"
                  >
                    Şifrəni unutmusunuz?
                  </Link>{" "}
                </p>
              </div>
            ) : (
              resetPassMutation.isError && (
                <div className="mt-2 relative">
                  <p className="text-red-500">
                    {resetPassMutation.error?.response?.data?.error?.message}
                  </p>
                </div>
              )
            )}
            <Button
              isLoading={resetPassMutation.isLoading}
              className="mt-4 w-full"
            >
              Təsdiqlə
            </Button>
          </form>
          <Link to="/login" className="text-sky-500 hover:underline">
            <FontAwesomeIcon className="me-2" icon={faArrowLeft} /> Giriş
            səhifəsinə qayıt
          </Link>
        </div>
   </AuthLayout>
  );
}
